import { flexRender, Table as ReactTable } from '@tanstack/react-table';
import { keyBy } from 'lodash';
import { useMemo, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

import { ButtonLink } from '../Button/ButtonLink';
import { Modal } from '../Modal/Modal';
import { BaseRow } from './Table';

import s from './ColumnPicker.module.scss';

type ColumnPickerGrouping = {
  categoryHeader?: string;
  columns: string[];
};

export type ColumnPickerModalGrouping = {
  left: ColumnPickerGrouping[];
  right: ColumnPickerGrouping[];
};

export const ColumnPicker = <TData extends BaseRow>({
  table,
  grouping,
}: {
  table: ReactTable<TData>;
  grouping: ColumnPickerModalGrouping;
}) => {
  const [showColumnVisibilityModal, setShowColumnVisibilityModal] = useState(false);
  const allColumns = table.getAllLeafColumns();
  const keyedColumns = useMemo(() => keyBy(allColumns, 'id'), [allColumns]);

  const renderColumn = (columnGroups: ColumnPickerGrouping[]) =>
    columnGroups.map((group) => (
      <div key={group.categoryHeader} className="mb-3">
        {group.categoryHeader && <h3 className="u-fs-3 fw-bold mb-2">{group.categoryHeader}</h3>}
        {group.columns.map((id) => {
          const column = keyedColumns[id];

          if (!column || !column.getCanHide()) {
            return null;
          }

          return (
            <Form.Check className="u-fs-2" key={column.id} id={column.id} type="checkbox">
              <Form.Check.Input
                type="checkbox"
                name={column.id}
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
              />
              {/* TODO - fix this when we upgrade to react-table-v8 */}
              <Form.Check.Label>{flexRender(column.columnDef.header, column as any)}</Form.Check.Label>
            </Form.Check>
          );
        })}
      </div>
    ));

  return (
    <>
      <ButtonLink variant="primary" onClick={() => setShowColumnVisibilityModal(true)}>
        Edit Columns
      </ButtonLink>

      <Modal
        showModal={showColumnVisibilityModal}
        className={s.columnPickerModal}
        header={<h2>Edit Columns</h2>}
        footer={
          <Button variant="primary" onClick={() => setShowColumnVisibilityModal(false)}>
            Close
          </Button>
        }
        onHide={() => setShowColumnVisibilityModal(false)}
        onClose={() => setShowColumnVisibilityModal(false)}
      >
        <>
          <Row>
            <Col>{renderColumn(grouping.left)}</Col>
            <Col>{renderColumn(grouping.right)}</Col>
          </Row>
        </>
      </Modal>
    </>
  );
};
